import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  X, Loader2, PhoneIncoming, PhoneOutgoing, Phone,
  Clock, ArrowRight, Sparkles, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import CallRecordingButton from '../CallRecordingButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISC_META = {
  D: { name: 'Dominance',     color: '#EF4444' },
  I: { name: 'Influence',     color: '#F59E0B' },
  S: { name: 'Steadiness',    color: '#10B981' },
  C: { name: 'Conscientious', color: '#3B82F6' },
};

const OUTCOME_META = {
  'Closed Deal':      { color: '#10B981', bg: '#ECFDF5' },
  'Likely Closed':    { color: '#14B8A6', bg: '#F0FDFA' },
  'Revivable Deal':   { color: '#F59E0B', bg: '#FFFBEB' },
  'Lost Deal':        { color: '#64748B', bg: '#F1F5F9' },
  'Unclear Outcome':  { color: '#94A3B8', bg: '#F8FAFC' },
};

// Simple linear ranking used to draw continuity arrows. A higher number means
// "closer to closed" — the arrow between two consecutive calls is coloured
// green if the caller moved up, red if they moved down.
const OUTCOME_RANK = {
  'Lost Deal': 0,
  'Unclear Outcome': 1,
  'Revivable Deal': 2,
  'Likely Closed': 3,
  'Closed Deal': 4,
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString([], {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso.slice(0, 16); }
};

const fmtDuration = (s) => {
  const n = Number(s) || 0;
  const mm = Math.floor(n / 60);
  const ss = String(n % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const DirectionIcon = ({ dir }) => {
  const cls = 'w-3.5 h-3.5';
  if (dir === 'inbound') return <PhoneIncoming className={`${cls} text-teal-600`} />;
  if (dir === 'outbound') return <PhoneOutgoing className={`${cls} text-indigo-500`} />;
  return <Phone className={`${cls} text-slate-400`} />;
};

const ScorePill = ({ score, band }) => {
  if (score == null) return <span className="text-xs text-slate-400 italic">Ungraded</span>;
  const bg = band === 'escalated' ? '#FEE2E2' : score >= 80 ? '#DCFCE7' : score >= 60 ? '#F0FDFA' : '#FEF3C7';
  const color = band === 'escalated' ? '#B91C1C' : score >= 80 ? '#166534' : score >= 60 ? '#0F766E' : '#B45309';
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold tabular-nums"
      style={{ background: bg, color }}
    >
      {score}
    </span>
  );
};

const OutcomeBadge = ({ type }) => {
  if (!type) return <span className="text-xs text-slate-400 italic">Unclassified</span>;
  const meta = OUTCOME_META[type] || OUTCOME_META['Unclear Outcome'];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{ background: meta.bg, color: meta.color }}
    >
      {type}
    </span>
  );
};

/**
 * Continuity arrow drawn between two consecutive timeline rows. Colour tells
 * the manager at a glance whether the deal warmed up or cooled down.
 */
const ContinuityArrow = ({ from, to }) => {
  if (!from || !to) return null;
  const a = OUTCOME_RANK[from] ?? null;
  const b = OUTCOME_RANK[to] ?? null;
  if (a == null || b == null) return null;
  let color = '#94A3B8';
  let label = 'Stable';
  if (b > a) { color = '#10B981'; label = `Warmed up (${from} → ${to})`; }
  else if (b < a) { color = '#F59E0B'; label = `Cooled down (${from} → ${to})`; }
  else { color = '#94A3B8'; label = `Repeated (${from})`; }
  return (
    <div
      className="flex items-center justify-center py-1"
      title={label}
      data-testid="caller-continuity-arrow"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color }}>
        <div className="w-px h-4" style={{ background: color }} />
        <ArrowRight className="w-3.5 h-3.5 rotate-90" />
        <span>{label}</span>
      </div>
    </div>
  );
};

export const CallerCallsModal = ({ phone, domainId, disc, dominantTraits, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { phone };
        if (domainId) params.domain_id = domainId;
        const r = await axios.get(`${API}/analytics/caller-intel/caller-timeline`, { params });
        if (!cancelled) setData(r.data);
      } catch (err) {
        console.error('caller-timeline fetch failed', err?.response || err);
        toast.error(err?.response?.data?.detail || 'Could not load call history for this caller.');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [phone, domainId, onClose]);

  const discMeta = DISC_META[disc] || null;

  // Ordered timeline (already oldest → newest from the API).
  const rows = useMemo(() => data?.timeline || [], [data]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={`caller-modal-${phone}`}
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-slate-200">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-bold text-slate-900 font-mono tracking-tight">{phone}</h3>
              {discMeta && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-white"
                  style={{ background: discMeta.color }}
                  title={discMeta.name}
                >
                  {disc} · {discMeta.name}
                </span>
              )}
              {data && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                  {data.total_calls} call{data.total_calls === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {dominantTraits && (
              <p className="text-xs text-slate-500 mt-1 truncate">{dominantTraits}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            data-testid="caller-modal-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-slate-400 italic">
              No calls on file for this caller.
            </div>
          ) : (
            <div className="space-y-1" data-testid="caller-timeline">
              {rows.map((row, i) => (
                <React.Fragment key={row.call_id}>
                  {i > 0 && (
                    <ContinuityArrow
                      from={rows[i - 1].outcome_type}
                      to={row.outcome_type}
                    />
                  )}
                  <div
                    className="block rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md bg-white p-4 transition-all group cursor-pointer"
                    data-testid={`caller-timeline-row-${row.call_id}`}
                    onClick={() => { onClose?.(); navigate(`/calls/${row.call_id}`); }}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                          <DirectionIcon dir={row.call_direction} />
                          <span className="font-semibold text-slate-800">{fmtDate(row.call_date)}</span>
                          <span className="text-slate-300">·</span>
                          <Clock className="w-3 h-3" />
                          <span className="tabular-nums">{fmtDuration(row.duration_seconds)}</span>
                          <span className="text-slate-300">·</span>
                          <span>Agent: <span className="text-slate-700 font-semibold">{row.agent_name}</span></span>
                          {row.call_type && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className="text-slate-500 italic">{row.call_type}</span>
                            </>
                          )}
                        </div>
                        {row.call_summary && (
                          <p className="text-sm text-slate-600 line-clamp-2">{row.call_summary}</p>
                        )}
                        {row.outcome_next_action && (
                          <p className="text-xs text-slate-500 italic flex items-start gap-1">
                            <Sparkles className="w-3 h-3 shrink-0 mt-0.5 text-amber-500" />
                            {row.outcome_next_action}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <ScorePill score={row.overall_score} band={row.score_band} />
                        <OutcomeBadge type={row.outcome_type} />
                        <div className="flex items-center gap-2">
                          <div onClick={(e) => e.stopPropagation()}>
                            <CallRecordingButton callId={row.call_id} size="xs" />
                          </div>
                          <span className="text-[11px] text-slate-400 flex items-center gap-1 group-hover:text-teal-600 transition-colors">
                            Open call <ExternalLink className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            Ordered oldest → newest. Click any call to open its full detail page.
          </p>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
